import {
    ApolloClient,
    ApolloError,
    DocumentNode,
    OperationVariables,
    QueryLazyOptions,
} from '@apollo/client'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { setElementMetadata, setFamilyProjectElementMetadata, setProjectElementMetadata } from 'api/graphQL/reactiveVars/element'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { LARGE_CONTENT_ITEM_NODES_THRESHOLD } from 'components/WorkspaceEditor/hooks/useTreeDataProcessor'
import { getActiveTabLabel } from 'components/WorkspaceEditor/utils'
import { isManufacturerOrBODHeader } from 'components/WorkspaceEditor/utils/manufacturerBodUtils'
import { TextContentElementType } from 'models/setProjectContentItemElementsInput'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { ContentItemViewNodesType } from '../../types/contentItemViewNode.type'
import {
    GetNuggetContentViewResponse,
    GetProductFamilyViewResponse,
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
    PartType,
} from '../../types/types'
import { ContentItemQueryVariables } from './useProductFetcher'

export interface BuildQueryVariablesProps {
    elementId: string
    projectId: string
    projectIdFromUrl?: string
    activeTab: number
    isTrackChangeToggleOn: boolean
    isPPF: boolean
    nodeId?: string
    isFromSMM?: boolean
    rootArticleContentItemIds: string[] | null
    activeTabIndex?: number
}

export interface FetchContentItemProps {
    partType: PartType
    loadProduct: (
        options?: QueryLazyOptions<OperationVariables> | undefined
    ) => void
    rootArticleContentItemIds?: string[]
}

type FetchContentItemResponse =
    | GetProjectProductFamilyViewResponse
    | GetProductFamilyViewResponse
    | GetNuggetContentViewResponse
    | GetProjectNuggetContentViewResponse
export interface ExecuteApolloQueryProps {
    apolloClient: ApolloClient<object>
    query: DocumentNode
    queryVariables: ContentItemQueryVariables
    callback?: (
        response:
            | GetProjectProductFamilyViewResponse
            | GetProjectNuggetContentViewResponse
    ) => Promise<void>
    onContentViewFetchSuccess: (
        response: FetchContentItemResponse,
        articlesLoading?: string[],
        shouldReturnContentItemViewNodes?: boolean,
        latestContentItemViewNodes?: Partial<ContentItemViewNodesType>[] | null,
        requestedPartType?: PartType
    ) => Promise<Partial<ContentItemViewNodesType>[] | undefined>
    articlesLoading: string[]
    finishLoading: () => void
    handleFetchError: (
        error: ApolloError,
        setArticlesLoading: (keys: string[]) => void,
        onError?: () => void
    ) => void
    setArticlesLoading: (keys: string[]) => void
    onError?: () => void
}

export const useProductFetcherUtils = ({
    overrideContentItemNodes = false,
    projectId = '',
}) => {
    const projectIdFromUrl = useParams<{ projectId?: string }>()?.projectId
    const {
        contentItemViewNodes,
        setArticlesLoading,
        setContentItemViewNodes,
        isFromSMM,
        activeTab,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        setArticlesLoading: state.actions.setArticlesLoading,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
        isFromSMM: state.isContentTeamFromStudioPage,
        activeTab: state.activeTab,
    }))
    const {
        elementId,
        isProjectProductFamily: isPPF,
        id: nodeId,
    } = useElementItemShallowSelector((state) => state.elementItemValue)

    /**
     * Checks if all items across two arrays have consistent partType values
     * @param firstArray - First array of content items
     * @param secondArray - Second array of content items
     * @returns true if all items have the same partType or if there are no partTypes defined
     */
    const haveConsistentPartTypes = useCallback(
        (
            firstArray: Partial<ContentItemViewNodesType>[],
            secondArray: Partial<ContentItemViewNodesType>[]
        ): boolean => {
            const extractPartTypes = (
                array: Partial<ContentItemViewNodesType>[]
            ) =>
                array
                    .filter((item) => item.partType && item.partType !== 'Part')
                    .map((item) => item.partType)

            const partTypes = new Set([
                ...extractPartTypes(firstArray),
                ...extractPartTypes(secondArray),
            ])

            // if we have 0 or 1 unique partTypes, all are the same
            return partTypes.size <= 1
        },
        []
    )

    /**
     * Merges two arrays of content items by applying updates.
     * @param existingItems - The current array of content items.
     * @param updatedItems - The array of content items containing updates.
     * @param overwrite - When true, only include items that have an update; otherwise, merge updates with existing items.
     * @returns The merged array of content items.
     */
    const mergeContentItems = useCallback(
        (
            existingItems: Partial<ContentItemViewNodesType>[],
            updatedItems: Partial<ContentItemViewNodesType>[]
        ): Partial<ContentItemViewNodesType>[] => {
            if (!haveConsistentPartTypes(existingItems, updatedItems)) {
                return updatedItems
            }
            // create a lookup map of updated items keyed by id
            const updatesById = new Map(
                updatedItems
                    .filter((item) => item.id)
                    .map((item) => [item.id, item])
            )
            // overwrite = false, retain all items (updating those that have a corresponding update).
            // overwrite = true, only retain items that have a corresponding update
            const mergedExistingItems = existingItems
                .filter((item) =>
                    overrideContentItemNodes ? updatesById.has(item.id!) : true
                )
                .map((item) =>
                    item.id && updatesById.has(item.id)
                        ? updatesById.get(item.id)!
                        : item
                )
            // find updated items that are new
            const existingIds = new Set(existingItems.map((item) => item.id))
            const additionalUpdatedItems = updatedItems.filter(
                (item) => item.id && !existingIds.has(item.id)
            )
            return [...mergedExistingItems, ...additionalUpdatedItems]
        },
        [overrideContentItemNodes, haveConsistentPartTypes]
    )

    /**
     * @description Handles API fetch errors and updates loading state
     * @param {ApolloError} error - The error returned from Apollo Client
     */
    const handleFetchError = useCallback(
        (
            error: ApolloError,
            setArticlesLoading: (keys: string[]) => void,
            onError?: () => void
        ) => {
            if (onError) {
                onError()
            }
            setArticlesLoading([])
            const summarizedError = summarizeApolloError(
                error,
                'WorkspaceEditor-getContentItemViewNodesQuery'
            )
            console.error(summarizedError)
        },
        [summarizeApolloError, setArticlesLoading]
    )

    /**
     * Calculates which article IDs should remain in the loading state
     * @param articlesLoading Current array of article IDs that are in loading state
     * @param nodes Content item view nodes from the API response
     * @returns Array of article IDs that should remain in loading state
     */
    const calculateLoadingArticleIds = useCallback(
        (
            articlesLoading: string[],
            nodes?: Partial<ContentItemViewNodesType>[] | null
        ): string[] => {
            if (!nodes?.length) return articlesLoading

            const articleIdSet = new Set(
                nodes
                    .filter((node) => node.textType === 'Article')
                    .map((node) => node.id)
                    .filter(Boolean) as string[]
            )
            // filter out articles that are now loaded
            const uniqueLoadingArticleIds = new Set(
                articlesLoading.filter((id) => !articleIdSet.has(id))
            )

            return Array.from(uniqueLoadingArticleIds)
        },
        []
    )

    /**
     * Append the bod pill content item node to the updated content item view nodes if it does not exist
     * @param updatedContentItemViewNodes The current updated content item view nodes
     */
    const appendBodContentIfNoBodPill = useCallback(
        (updatedContentItemViewNodes: Partial<ContentItemViewNodesType>[]) => {
            const newContentItemViewNodes = [...updatedContentItemViewNodes]
            try {
                /**
                 * We only needs pills data of manufacturer content items so filtering out the manufacturer content item
                 * Also exclude BOD pill copies (nodes with refBodItemId or _bod_ in their ID)
                 */
                const manufacturerContentItemViewNodes = newContentItemViewNodes.filter(
                    (node) => {
                        // Only process manufacturer nodes
                        if (!node.isManufacturers) return false

                        // Exclude BOD pill copies - they have refBodItemId set or _bod_ in ID
                        if (node.refBodItemId || node.id?.includes('_bod_')) return false

                        // Use centralized utility to check if it's a header
                        // Headers should be excluded, pills should be included
                        return !isManufacturerOrBODHeader(node)
                    }
                )

                if (manufacturerContentItemViewNodes.length === 0) {
                    return newContentItemViewNodes
                }

                const bodContentItemViewNodes = newContentItemViewNodes.filter(
                    (node) => node.isBOD
                )

                if (bodContentItemViewNodes.length === 0) {
                    return newContentItemViewNodes
                }

                bodContentItemViewNodes.forEach((bodContent) => {
                    const bodContentItemViewNode = newContentItemViewNodes.find(
                        (x) => x.dtvItemId === bodContent.dtvItemId
                    )

                    if (!bodContentItemViewNode) return

                    // Get existing BOD pill IDs for this BOD node by extracting original manufacturer IDs
                    const bodDtvItemId = bodContentItemViewNode.dtvItemId
                    const bodSuffix = `_bod_${bodDtvItemId}`

                    // Find existing BOD pills for this BOD node
                    const existingBodPillNodes = newContentItemViewNodes.filter(
                        (x) => x.refBodItemId === bodDtvItemId
                    )

                    const existingBodPillIds = new Set(
                        existingBodPillNodes.map((x) => {
                            // Extract original manufacturer ID by removing the _bod_{bodDtvItemId} suffix
                            if (x.id?.endsWith(bodSuffix)) {
                                return x.id.slice(0, -bodSuffix.length)
                            }
                            return x.id
                        })
                    )

                    // UPDATE existing BOD pills with current states
                    // Create Map for O(1) lookups instead of O(n) findIndex
                    const nodeIndexMap = new Map(
                        newContentItemViewNodes.map((node, idx) => [node.id, idx])
                    )

                    manufacturerContentItemViewNodes.forEach((manufacturerPill) => {
                        if (manufacturerPill.dtvParentId === bodContentItemViewNode.dtvParentId) {
                            const bodPillId = `${manufacturerPill.id}${bodSuffix}`
                            const existingBodPillIndex = nodeIndexMap.get(bodPillId)

                            if (existingBodPillIndex !== undefined) {
                                // Update existing BOD pill: isHidden follows BOD paragraph, isPillSelected follows manufacturer
                                newContentItemViewNodes[existingBodPillIndex] = {
                                    ...newContentItemViewNodes[existingBodPillIndex],
                                    isHidden: bodContentItemViewNode.isHidden,
                                    isPillSelected: manufacturerPill.isPillSelected,
                                }
                            }
                        }
                    })

                    // Find manufacturer pills that share the same dtvParentId and don't have BOD copies yet
                    const manufacturerPillsToAdd = manufacturerContentItemViewNodes.filter(
                        (manufacturerContentItemViewNode) =>
                            manufacturerContentItemViewNode.dtvParentId ===
                                bodContentItemViewNode.dtvParentId &&
                            !existingBodPillIds.has(
                                manufacturerContentItemViewNode.id
                            )
                    )

                    if (manufacturerPillsToAdd.length > 0) {
                        let index = newContentItemViewNodes.indexOf(
                            bodContentItemViewNode
                        )

                        // Find the last BOD pill position to insert after it (reuse existingBodPillNodes)
                        if (existingBodPillNodes.length > 0) {
                            const lastBodPill =
                                existingBodPillNodes[existingBodPillNodes.length - 1]
                            const lastBodPillIndex = newContentItemViewNodes.indexOf(
                                lastBodPill
                            )
                            if (lastBodPillIndex > index) {
                                index = lastBodPillIndex
                            }
                        }

                        const updatedPillsContentItemViewNodes: Partial<
                            ContentItemViewNodesType
                        >[] = []

                        manufacturerPillsToAdd.forEach(
                            (filteredManufacturerContentItemViewNode) => {
                                updatedPillsContentItemViewNodes.push({
                                    ...filteredManufacturerContentItemViewNode,
                                    refBodItemId: bodDtvItemId,
                                    id: `${filteredManufacturerContentItemViewNode.id}${bodSuffix}`,
                                    isHidden: bodContentItemViewNode.isHidden,
                                })
                            }
                        )

                        newContentItemViewNodes.splice(
                            index + 1,
                            0,
                            ...updatedPillsContentItemViewNodes
                        )
                    }
                })
            } catch (error) {
                console.error(error)
            } finally {
                return newContentItemViewNodes
            }
        },
        []
    )

    /**
     * Handle successful API fetch and update loading state
     * @param {GetProjectProductFamilyViewResponse | GetProductFamilyViewResponse} response - The response from the API
     * @param {Partial<ContentItemViewNodesType>[]} contentItemViewNodes - The current content item view nodes
     * @param {Set<string>} setArticlesLoading - The set of article IDs currently in loading state
     * @param {boolean} shouldReturnContentItemViewNodes - Flag to determine if the content item nodes should be returned
     * @param {Partial<ContentItemViewNodesType>[]} latestContentItemViewNodes - The latest content item view nodes
     * @returns {Promise<Partial<ContentItemViewNodesType>[] | undefined>} - The updated content item view nodes or undefined if not requested
     *           If the content item view nodes should not be returned, returns undefined
     * @description Handles the success response from the API and updates the content item view nodes
     * */
    const onContentViewFetchSuccess = useCallback(
        async (
            response: FetchContentItemResponse,
            articlesLoading: string[] = [],
            shouldReturnContentItemViewNodes = false,
            latestContentItemViewNodes:
                | Partial<ContentItemViewNodesType>[]
                | null = null,
            requestedPartType?: PartType
        ): Promise<Partial<ContentItemViewNodesType>[] | undefined> => {
            // Validate that the response matches the current active tab to prevent race conditions
            // TODO: remove this condition when implementing full view
            if (requestedPartType) {
                const currentPartType = getActiveTabLabel(activeTab)
                if (requestedPartType !== currentPartType) {
                    // Ignoring state data when request and response part type is not the same
                    return undefined
                }
            }

            let responseContentItemViewNodes = null

            if ('getProjectProductFamilyView' in response) {
                responseContentItemViewNodes =
                    response.getProjectProductFamilyView?.contentItemViewNodes
                setElementMetadata(
                    response.getProjectProductFamilyView?.id ?? '',
                    response.getProjectProductFamilyView?.metadata
                )
                // Update projectMetadata with LOS values for immediate display
                const familyMetadata = response.getProjectProductFamilyView?.projectElementMetadata
                if (familyMetadata) {
                    setProjectElementMetadata(familyMetadata)
                    // Also set family-specific metadata for LOS icon display
                    setFamilyProjectElementMetadata(familyMetadata)
                }
            } else if ('productFamilyView' in response) {
                responseContentItemViewNodes =
                    response.productFamilyView?.contentItemViewNodes
            } else if ('nugget' in response) {
                responseContentItemViewNodes =
                    response.nugget?.contentItemViewNodes
            } else if ('getProjectNugget' in response) {
                responseContentItemViewNodes =
                    response.getProjectNugget?.contentItemViewNodes
                setElementMetadata(
                    response.getProjectNugget?.id ?? '',
                    response.getProjectNugget?.metadata
                )
                // Update projectMetadata with LOS values for immediate display
                const nuggetMetadata = response.getProjectNugget?.projectElementMetadata
                if (nuggetMetadata) {
                    setProjectElementMetadata(nuggetMetadata)
                }
            }

            let updatedContentItemViewNodes = mergeContentItems(
                latestContentItemViewNodes || contentItemViewNodes,
                responseContentItemViewNodes || []
            )

            updatedContentItemViewNodes = appendBodContentIfNoBodPill(
                updatedContentItemViewNodes
            )

            if (shouldReturnContentItemViewNodes) {
                return updatedContentItemViewNodes
            }
            setContentItemViewNodes(updatedContentItemViewNodes)

            // don't clear articlesLoading immediately for large datasets
            const isLargeDataset =
                updatedContentItemViewNodes.length >
                LARGE_CONTENT_ITEM_NODES_THRESHOLD

            if (!isLargeDataset) {
                // small dataset - clear immediately as before
                const articleIds = calculateLoadingArticleIds(
                    articlesLoading,
                    responseContentItemViewNodes
                )
                setArticlesLoading(articleIds)
            }
        },
        [
            mergeContentItems,
            contentItemViewNodes,
            appendBodContentIfNoBodPill,
            setContentItemViewNodes,
            setArticlesLoading,
            activeTab,
        ]
    )

    /**
     * Creates a fetch function for either product family or project product family
     * @param {PartType} partType - Part type
     * @param {string[]} [rootArticleContentItemIds] - (Optional) Array of root article content item IDs
     */
    /** */
    const fetchContentItem = useCallback(
        async (fetchContentItemProps: FetchContentItemProps): Promise<void> => {
            const {
                rootArticleContentItemIds,
                partType,
                loadProduct,
            } = fetchContentItemProps

            const queryVariables: Record<string, any> = {
                id: elementId,
                projectId: projectId || projectIdFromUrl,
                partType,
                isTrackChangeToggleOn: true,
                ...(rootArticleContentItemIds && { rootArticleContentItemIds }),
                ...(isPPF && { elementId }),
                ...(nodeId && { nodeId }),
                ...(isFromSMM && { isFromSMM }),
            }

            return loadProduct({
                variables: queryVariables,
            })
        },
        [elementId, projectId, isPPF, nodeId, isFromSMM, contentItemViewNodes]
    )

    const buildQueryVariables = (
        props: BuildQueryVariablesProps
    ): ContentItemQueryVariables => {
        const {
            elementId,
            projectId,
            projectIdFromUrl,
            activeTab,
            isTrackChangeToggleOn,
            isPPF,
            nodeId,
            isFromSMM,
            rootArticleContentItemIds,
            activeTabIndex,
        } = props

        return {
            id: elementId,
            projectId: projectId || projectIdFromUrl,
            partType: getActiveTabLabel(activeTabIndex ?? activeTab),
            rootArticleContentItemIds,
            isTrackChangeToggleOn: isTrackChangeToggleOn || false,
            ...(isPPF && { elementId }),
            ...(nodeId && { nodeId }),
            ...(isFromSMM && { isFromSMM }),
        }
    }

    const executeApolloQuery = async (props: ExecuteApolloQueryProps) => {
        const {
            apolloClient,
            query,
            queryVariables,
            callback,
            onContentViewFetchSuccess,
            articlesLoading,
            finishLoading,
            handleFetchError,
            setArticlesLoading,
            onError,
        } = props

        try {
            const result = await apolloClient.query({
                query,
                variables: queryVariables,
                fetchPolicy: 'no-cache',
            })

            if (callback) {
                await callback(result.data)
            } else {
                await onContentViewFetchSuccess(
                    result.data,
                    articlesLoading,
                    false,
                    null,
                    queryVariables.partType
                )
                finishLoading()
            }
        } catch (error) {
            handleFetchError(error as ApolloError, setArticlesLoading, onError)
            finishLoading()
        }
    }

    return {
        mergeContentItems,
        handleFetchError,
        onContentViewFetchSuccess,
        appendBodContentIfNoBodPill,
        fetchContentItem,
        haveConsistentPartTypes,
        buildQueryVariables,
        executeApolloQuery,
    }
}
